import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

type Size = 'sm' | 'md' | 'lg';

const KNOWN_VENDORS: Record<string, { name: string; monogram: string; markClassName: string }> = {
  STEDI: {
    name: 'Stedi',
    monogram: 'S',
    markClassName:
      'bg-uniform-gray-900 text-white dark:bg-uniform-gray-100 dark:text-uniform-gray-900'
  },
  FRONTRUNNER: {
    name: 'FrontRunner',
    monogram: 'FR',
    markClassName: 'bg-uniform-gray-500 text-white'
  }
};

const vendorLogoVariants = tv({
  slots: {
    root: 'inline-flex items-center gap-1.5',
    mark: 'inline-flex shrink-0 select-none items-center justify-center rounded font-bold leading-none',
    name: 'text-uniform-gray-700 dark:text-uniform-gray-300'
  },
  variants: {
    size: {
      sm: { mark: 'h-3.5 w-3.5 text-[7.5px]', name: 'text-xs' },
      md: { mark: 'h-4 w-4 text-[8.5px]', name: 'text-sm' },
      lg: { mark: 'h-5 w-5 text-[10px]', name: 'text-sm' }
    }
  },
  defaultVariants: { size: 'md' }
});

type VendorLogoVariants = VariantProps<typeof vendorLogoVariants>;

interface VendorLogoProps {
  /** Vendor identifier, e.g. "STEDI", "FRONTRUNNER" or a display name like "FrontRunner". */
  source?: string | null;
  size?: Size;
  /** Renders the vendor's name after the mark. */
  showName?: boolean;
  className?: string;
}

const vendorKey = (source: string) => source.toUpperCase().replace(/[^A-Z]/g, '');

const vendorDisplayName = (source?: string | null): string =>
  (source ? KNOWN_VENDORS[vendorKey(source)]?.name : undefined) ?? source ?? 'Unknown vendor';

function VendorLogo({ source, size = 'md', showName = false, className }: VendorLogoProps) {
  const { root, mark, name } = vendorLogoVariants({ size });

  const vendor = source ? KNOWN_VENDORS[vendorKey(source)] : undefined;
  const displayName = vendorDisplayName(source);
  const monogram = vendor?.monogram ?? (source?.trim().charAt(0).toUpperCase() || '?');
  const markClassName = vendor?.markClassName ?? 'bg-uniform-gray-300 text-uniform-gray-700';

  return (
    <span className={cn(root(), className)}>
      <span
        aria-hidden={showName || undefined}
        aria-label={showName ? undefined : displayName}
        role={showName ? undefined : 'img'}
        className={cn(mark(), markClassName)}>
        {monogram}
      </span>
      {showName && <span className={name()}>{displayName}</span>}
    </span>
  );
}

export { VendorLogo, vendorDisplayName, vendorLogoVariants };
export type { VendorLogoProps, VendorLogoVariants };
